import { useEffect, useState } from "react";
import api from "../lib/api";
import { formatEUR, formatEURDecimal, SPANISH_MONTHS_SHORT } from "../lib/format";
import { ArrowUpRight, ArrowDownRight, Wallet, TrendingUp, Layers, FileSpreadsheet } from "lucide-react";

function KPI({ label, value, hint, accent, icon: Icon, testid }) {
  return (
    <div className="border border-[var(--border)] bg-white p-6" data-testid={testid}>
      <div className="flex items-center justify-between text-[10px] uppercase tracking-[0.2em] text-[var(--ink-muted)] font-mono-num">
        <span>{label}</span>
        {Icon && <Icon size={14} strokeWidth={1.6} />}
      </div>
      <div
        className={`mt-3 font-display font-black tracking-tight text-3xl leading-none ${
          accent === "pos" ? "text-emerald-700" : accent === "neg" ? "text-red-700" : "text-[var(--ink)]"
        }`}
      >
        {value}
      </div>
      {hint && <div className="mt-2 text-xs text-[var(--ink-muted)]">{hint}</div>}
    </div>
  );
}

export default function Dashboard() {
  const [year, setYear] = useState(new Date().getFullYear());
  const [years, setYears] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const yr = await api.get("/cashflow/years");
        const list = yr.data.years || [];
        if (!list.includes(year)) list.push(year);
        list.sort((a, b) => a - b);
        setYears(list);
      } catch (e) {}
    })();
  }, []);

  useEffect(() => {
    setLoading(true);
    api.get(`/dashboard?year=${year}`)
      .then(r => setData(r.data))
      .finally(() => setLoading(false));
  }, [year]);

  const max = data ? Math.max(...data.monthly_series.map(m => Math.max(m.ingreso, m.gasto)), 1) : 1;

  return (
    <div className="p-8" data-testid="dashboard-page">
      <div className="flex items-end justify-between mb-8 gap-4 flex-wrap">
        <div>
          <div className="font-mono-num text-[10px] uppercase tracking-[0.25em] text-[var(--ink-muted)]">Resumen</div>
          <h1 className="font-display text-4xl font-black tracking-tight">Estado de tesorería.</h1>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-[var(--ink-muted)] font-mono-num uppercase tracking-widest">Año</span>
          <select
            data-testid="year-selector"
            className="border border-[var(--border)] bg-white px-3 py-2 text-sm font-mono-num focus:outline-none focus:border-black"
            value={year}
            onChange={(e) => setYear(Number(e.target.value))}
          >
            {years.map(y => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
        </div>
      </div>

      {loading || !data ? (
        <div className="text-sm text-[var(--ink-muted)] font-mono-num" data-testid="dashboard-loading">Cargando…</div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <KPI
              testid="kpi-saldo"
              label="Saldo total bancos"
              value={formatEUR(data.saldo_total_bancos)}
              icon={Wallet}
              hint="Suma actual de las cuentas registradas"
            />
            <KPI
              testid="kpi-cashflow-fin"
              label={`Cashflow proyectado ${year}`}
              value={formatEUR(data.cashflow_fin_anio)}
              accent={data.cashflow_fin_anio >= 0 ? "pos" : "neg"}
              icon={TrendingUp}
              hint="Saldo + ingresos − gastos del año"
            />
            <KPI
              testid="kpi-ingresos"
              label="Ingresos previstos"
              value={formatEUR(data.total_ingresos)}
              accent="pos"
              icon={ArrowUpRight}
            />
            <KPI
              testid="kpi-gastos"
              label="Gastos previstos"
              value={formatEUR(data.total_gastos)}
              accent="neg"
              icon={ArrowDownRight}
            />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="lg:col-span-2 border border-[var(--border)] bg-white p-6" data-testid="monthly-chart">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <div className="font-mono-num text-[10px] uppercase tracking-[0.2em] text-[var(--ink-muted)]">Evolución mensual</div>
                  <h2 className="font-display text-xl font-bold tracking-tight">Ingresos vs Gastos · {year}</h2>
                </div>
                <div className="flex gap-3 text-[10px] uppercase tracking-widest font-mono-num">
                  <span className="flex items-center gap-1.5"><span className="h-2 w-2 bg-emerald-700"></span>Ingreso</span>
                  <span className="flex items-center gap-1.5"><span className="h-2 w-2 bg-red-700"></span>Gasto</span>
                </div>
              </div>
              <div className="grid grid-cols-12 gap-2 h-48 items-end">
                {data.monthly_series.map((m, i) => (
                  <div key={i} className="flex flex-col items-center gap-1 group">
                    <div className="w-full flex items-end gap-0.5 h-44">
                      <div
                        className="flex-1 bg-emerald-700 transition-all"
                        style={{ height: `${(m.ingreso / max) * 100}%` }}
                        title={`${formatEUR(m.ingreso)}`}
                      />
                      <div
                        className="flex-1 bg-red-700 transition-all"
                        style={{ height: `${(m.gasto / max) * 100}%` }}
                        title={`${formatEUR(m.gasto)}`}
                      />
                    </div>
                    <div className="text-[10px] uppercase font-mono-num text-[var(--ink-muted)]">
                      {SPANISH_MONTHS_SHORT[i]}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="border border-[var(--border)] bg-white p-6" data-testid="saldo-evolucion">
              <div className="font-mono-num text-[10px] uppercase tracking-[0.2em] text-[var(--ink-muted)]">Saldo acumulado fin de mes</div>
              <h2 className="font-display text-xl font-bold tracking-tight mb-4">Proyección saldo</h2>
              <ul className="space-y-1">
                {data.monthly_series.map((m, i) => (
                  <li key={i} className="flex items-center justify-between text-xs py-1 border-b border-[var(--border)] last:border-0">
                    <span className="font-mono-num uppercase text-[var(--ink-muted)]">{SPANISH_MONTHS_SHORT[i]}</span>
                    <span className={`font-mono-num font-medium ${m.saldo >= 0 ? "text-[var(--ink)]" : "text-red-700"}`}>
                      {formatEURDecimal(m.saldo)}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
            <KPI testid="kpi-proyectos" label="Proyectos con movimientos" value={data.n_projects} icon={Layers} />
            <KPI testid="kpi-movimientos" label="Movimientos totales" value={data.n_entries} icon={FileSpreadsheet} />
            <KPI testid="kpi-quincena" label="Quincena actual" value={`${SPANISH_MONTHS_SHORT[data.current_month-1]} · Q${data.current_quincena}`} />
          </div>
        </>
      )}
    </div>
  );
}
